import React, { useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ButtonGroup from '@mui/material/ButtonGroup';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { Result } from '@peercolab/engine/esm';
import { Published } from '../peer-colab-model/PetKey/Main/Model/1_0_Initial/Backoffice/Core/PathItems';

export type PublishStatus = 'draft' | 'published';

interface PublishStatusComponentProps {
  status: Published;
  onTransition: (action: string) => Promise<Result<any>>;
}

const statusLabels: Record<PublishStatus, string> = {
  draft: 'Draft',
  published: 'Published',
};

const statusColors: Record<PublishStatus | 'hasChanges', 'default' | 'success' | 'warning' | 'error'> = {
  draft: 'default',
  published: 'success',
  hasChanges: 'warning',
};

const PublishStatusComponent: React.FC<PublishStatusComponentProps> = ({ status, onTransition }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);
  const [confirmAction, setConfirmAction] = useState<null | 'publish' | 'unpublish' | 'revert'>(null);
  let chipIcon: React.ReactNode = undefined;
  let chipLabel = statusLabels[status.state as PublishStatus];
  let chipColor = statusColors[status.state as PublishStatus];

  if (status.isDirty) {
    chipLabel = 'Has Changes';
    chipColor = statusColors['hasChanges'];
    chipIcon = <NotificationsActiveIcon fontSize="small" sx={{ ml: 0.5 }} />;
  } else if (!status.lastPublishedAt) {
    chipIcon = <InfoOutlinedIcon fontSize="small" sx={{ ml: 0.5 }} />;
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      <Chip
        label={chipLabel}
        color={chipColor}
        variant="outlined"
        icon={chipIcon}
      />
      {status.state === 'draft' && !status.lastPublishedAt && (
        <Button variant="contained" color="primary" onClick={() => setConfirmAction('publish')}>Publish</Button>
      )}
      {status.isDirty && (
        <>
          <ButtonGroup variant="contained" color="primary">
            <Button onClick={() => setConfirmAction('publish')}>Publish Changes</Button>
            <Button onClick={e => setAnchorEl(e.currentTarget)} sx={{ minWidth: 40 }}>
              <ArrowDropDownIcon />
            </Button>
          </ButtonGroup>
          <Menu
            anchorEl={anchorEl}
            open={menuOpen}
            onClose={() => setAnchorEl(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            {status.lastPublishedAt && (
              <MenuItem onClick={() => { setAnchorEl(null); setConfirmAction('revert'); }}>
                Revert Changes
              </MenuItem>
            )}
            <MenuItem onClick={() => { setAnchorEl(null); setConfirmAction('unpublish'); }}>
              Unpublish
            </MenuItem>
          </Menu>
        </>
      )}
      {status.state === 'published' && !status.isDirty && (
        <Button variant="outlined" color="error" onClick={() => setConfirmAction('unpublish')}>Unpublish</Button>
      )}

      {/* Confirmation Dialog for all actions */}
      <Dialog open={!!confirmAction} onClose={() => setConfirmAction(null)}>
        <DialogTitle>
          {confirmAction === 'publish' && 'Confirm Publish'}
          {confirmAction === 'unpublish' && 'Confirm Unpublish'}
          {confirmAction === 'revert' && 'Confirm Revert Changes'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmAction === 'publish' && 'Are you sure you want to publish these changes?'}
            {confirmAction === 'unpublish' && 'Are you sure you want to unpublish? This will remove the published state.'}
            {confirmAction === 'revert' && 'Are you sure you want to revert all changes since last publish?'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmAction(null)} color="primary">Cancel</Button>
          {confirmAction === 'publish' && (
            <Button onClick={async () => { 
              setConfirmAction(null); 
              await onTransition('publish'); 
            }} color="primary" variant="contained">Publish</Button>
          )}
          {confirmAction === 'unpublish' && (
            <Button onClick={async () => { 
              setConfirmAction(null); 
              await onTransition('unpublish'); 
            }} color="error" variant="contained">Unpublish</Button>
          )}
          {confirmAction === 'revert' && (
            <Button onClick={async () => { 
              setConfirmAction(null); 
              await onTransition('revert'); 
            }} color="warning" variant="contained">Revert</Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PublishStatusComponent;
